import type { FC } from 'react'
import type { RewardPackage } from 'types/rewards'
import type { User } from 'types/user'
import type { Period } from 'types/periods'

import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'

import AnimateButton from 'components/@extended/AnimateButton'
import { TextField } from 'components/@extended/TextField'
import SinglePeriodSelector from 'components/SinglePeriodSelector'
import SingleOrganizationSelector from 'components/SingleOrganizationSelector'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useAuth from 'hooks/useAuth'
import { useSearchUsers } from 'hooks/useUsers'
import { usePeriods } from 'hooks/usePeriods'
import { getRootOrganizationId } from 'types/user-profile'

interface AssignPackageDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (userId: string, periodId: string, rootPeriodId: string, organizationId: string) => Promise<void>
  pkg: RewardPackage | null
  isLoading: boolean
}

const AssignPackageDialog: FC<AssignPackageDialogProps> = ({ open, onClose, onSubmit, pkg, isLoading }) => {
  const { token, profile } = useAuth()
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('')

  useEffect(() => {
    if (profile) {
      const orgId = getRootOrganizationId(profile)
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile])

  // Handle team change - clear selected user when team changes
  const handleOrganizationChange = useCallback((orgId: string) => {
    setSelectedOrganizationId(orgId)
    setSelectedUser(null)
  }, [])

  // Search users who are members of the selected team
  const { users, isLoading: isLoadingUsers } = useSearchUsers(
    token!,
    {
      organizationIds: selectedOrganizationId ? [selectedOrganizationId] : [],
      perPage: 1000,
    },
    !!token && open && !!selectedOrganizationId
  )

  // Fetch periods
  const { periods, isLoadingPeriods } = usePeriods(token!, rootOrganizationId, !!rootOrganizationId && !!token && open)

  // Find root period for selected period
  const selectedPeriod = useMemo(() => {
    if (!selectedPeriodId || !periods?.periods) return null
    return periods.periods.find((p: Period) => p.id === selectedPeriodId)
  }, [selectedPeriodId, periods])

  const rootPeriodId = useMemo(() => {
    if (!selectedPeriod) return ''
    return selectedPeriod.rootPeriodId || selectedPeriod.id
  }, [selectedPeriod])

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedOrganizationId('')
      setSelectedUser(null)
      setSelectedPeriodId('')
    }
  }, [open])

  const handleSubmit = useCallback(async () => {
    if (!selectedUser || !selectedPeriodId || !rootPeriodId || !selectedOrganizationId) return
    await onSubmit(selectedUser.id, selectedPeriodId, rootPeriodId, selectedOrganizationId)
  }, [selectedUser, selectedPeriodId, rootPeriodId, selectedOrganizationId, onSubmit])

  const canSubmit = selectedUser && selectedPeriodId && rootPeriodId && selectedOrganizationId && !isLoading

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign Package to Employee
        {pkg && (
          <Typography variant="body2" color="textSecondary">
            {pkg.name}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {profile && (
            <Stack spacing={1}>
              <SingleOrganizationSelector
                userProfile={profile}
                selectedOrganizationId={selectedOrganizationId}
                handleChange={handleOrganizationChange}
                label="Select Team"
                placeholder="Select a team"
                disableAutoSelection
                variant="outlined"
              />
            </Stack>
          )}

          <Autocomplete
            id="employee-select"
            options={users || []}
            fullWidth
            autoHighlight
            getOptionLabel={(option) => (option ? `${option.firstName} ${option.lastName}` : '')}
            value={selectedUser}
            onChange={(_, newValue) => setSelectedUser(newValue)}
            loading={isLoadingUsers}
            disabled={!selectedOrganizationId}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Employee"
                placeholder={selectedOrganizationId ? 'Select Employee' : 'Select a team first'}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password',
                }}
                variant="outlined"
                shrink
                pill
              />
            )}
          />

          <SinglePeriodSelector
            periods={periods?.periods || []}
            value={selectedPeriodId}
            onChange={setSelectedPeriodId}
            label="Select Period"
            placeholder="Select a period"
            disabled={isLoadingPeriods}
            disableClosed
            rootPeriodOnly
            pill
            shrink
          />

          <Typography variant="body2" color="textSecondary">
            This will apply all rewards from this package to the selected employee for the chosen period.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AnimateButton>
          <Button variant="contained" color="warning" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
        </AnimateButton>
        <AnimateButton>
          <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Assign Package'}
          </Button>
        </AnimateButton>
      </DialogActions>
    </Dialog>
  )
}

export default AssignPackageDialog
